import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import SocialAuthButtons from '../components/auth/SocialAuthButtons';
import { AuthShell } from './SignupPage';
import PageSEO from '../components/PageSEO';

export default function LoginPage() {
  const navigate          = useNavigate();
  const location          = useLocation();
  const { signInWithEmail, user, loading } = useAuth();

  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting,   setSubmitting]   = useState(false);
  const [error,        setError]        = useState<string | null>(null);

  // Where to send the user after login — respects the "from" state set by ProtectedRoute
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/dashboard';

  // Already logged in → redirect
  useEffect(() => {
    if (!loading && user) navigate(from, { replace: true });
  }, [user, loading, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const err = await signInWithEmail(email.trim().toLowerCase(), password);
      if (err) {
        // Map Supabase error messages to user-friendly copy
        const msg = err.message.toLowerCase();
        if (msg.includes('invalid login') || msg.includes('invalid credentials')) {
          setError('Email or password is incorrect. Please try again.');
        } else if (msg.includes('email not confirmed')) {
          setError('Please check your email and click the confirmation link before signing in.');
        } else {
          setError(err.message);
        }
        setSubmitting(false);
        return;
      }
      // On success, onAuthStateChange fires → AuthContext updates → useEffect above redirects
      // Safety net: if navigation hasn't happened in 8s, stop the spinner
      setTimeout(() => setSubmitting(false), 8000);
    } catch {
      setError('Unable to connect. Please check your connection and try again.');
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <PageSEO
        title="Log In | MomentSent"
        description="Log in to your MomentSent account to manage your contacts, upcoming occasions, and card settings."
        canonical="https://momentsent.com/login"
      />
      <div className="text-center mb-7">
        <h1 className="font-heading text-3xl text-navy mb-2">Welcome back</h1>
        <p className="font-body text-sm text-gray-500">
          Sign in to your MomentSent account
        </p>
      </div>

      {/* Social auth */}
      <SocialAuthButtons mode="signin" />

      {/* Divider */}
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="font-body text-xs text-gray-400">or</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {/* Email/password form */}
      <form onSubmit={handleSubmit} noValidate className="space-y-3">

        <div>
          <label htmlFor="email" className="sr-only">Email address</label>
          <input
            id="email"
            type="email"
            placeholder="Email address"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={submitting}
            className="input"
          />
        </div>

        <div className="relative">
          <label htmlFor="password" className="sr-only">Password</label>
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={submitting}
            className="input pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-navy transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        {/* Forgot password */}
        <div className="flex justify-end">
          <ForgotPasswordLink email={email} />
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 animate-fade-in">
            <p className="font-body text-sm text-red-600">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !email.trim() || !password}
          className="w-full btn-primary flex items-center justify-center gap-2
                     disabled:opacity-50 disabled:cursor-not-allowed
                     disabled:hover:shadow-none disabled:hover:translate-y-0"
        >
          {submitting ? (
            <><Loader2 size={16} className="animate-spin" /><span>Signing in…</span></>
          ) : (
            <span>Sign in</span>
          )}
        </button>

      </form>

      {/* Sign up link */}
      <p className="mt-5 text-center font-body text-sm text-gray-500">
        Don't have an account?{' '}
        <Link to="/signup" className="text-sage font-medium hover:underline">
          Start free trial
        </Link>
      </p>
    </AuthShell>
  );
}

// ── Forgot password (inline, no separate page) ────────────────────────────────

function ForgotPasswordLink({ email }: { email: string }) {
  const [sent,    setSent]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [noEmail, setNoEmail] = useState(false);

  const handleReset = async () => {
    if (!email.trim()) {
      setNoEmail(true);
      return;
    }
    setNoEmail(false);
    setLoading(true);
    await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });
    setSent(true);
    setLoading(false);
  };

  if (sent) {
    return <span className="font-body text-xs text-sage">Reset link sent, check your inbox.</span>;
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={handleReset}
        disabled={loading}
        className="font-body text-xs text-gray-400 hover:text-sage transition-colors disabled:opacity-50"
      >
        {loading ? 'Sending…' : 'Forgot password?'}
      </button>
      {noEmail && (
        <p className="font-body text-xs text-red-500 animate-fade-in">
          Enter your email above first.
        </p>
      )}
    </div>
  );
}
